/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import { Directionality } from '@angular/cdk/bidi';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Platform } from '@angular/cdk/platform';
import { takeUntil } from '@angular/cdk/rxjs';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChildren, Directive, ElementRef, forwardRef, Inject, Input, NgZone, Optional, Renderer2, ViewChild, ViewEncapsulation, } from '@angular/core';
import { MAT_RIPPLE_GLOBAL_OPTIONS, MatRipple, mixinColor, mixinDisabled, mixinDisableRipple, } from '@angular/material/core';
import { merge } from 'rxjs/observable/merge';
import { of as observableOf } from 'rxjs/observable/of';
import { Subject } from 'rxjs/Subject';
import { MatInkBar } from '../ink-bar';
/**
 * \@docs-private
 */
export class MatTabNavBase {
    /**
     * @param {?} _renderer
     * @param {?} _elementRef
     */
    constructor(_renderer, _elementRef) {
        this._renderer = _renderer;
        this._elementRef = _elementRef;
    }
}
function MatTabNavBase_tsickle_Closure_declarations() {
    /** @type {?} */
    MatTabNavBase.prototype._renderer;
    /** @type {?} */
    MatTabNavBase.prototype._elementRef;
}
export const /** @type {?} */ _MatTabNavMixinBase = mixinDisableRipple(mixinColor(MatTabNavBase, 'primary'));
/**
 * Navigation component matching the styles of the tab group header.
 * Provides anchored navigation with animated ink bar.
 */
export class MatTabNav extends _MatTabNavMixinBase {
    /**
     * @param {?} renderer
     * @param {?} elementRef
     * @param {?} _dir
     * @param {?} _ngZone
     * @param {?} _changeDetectorRef
     * @param {?} _viewportRuler
     */
    constructor(renderer, elementRef, _dir, _ngZone, _changeDetectorRef, _viewportRuler) {
        super(renderer, elementRef);
        this._dir = _dir;
        this._ngZone = _ngZone;
        this._changeDetectorRef = _changeDetectorRef;
        this._viewportRuler = _viewportRuler;
        /**
         * Subject that emits when the component has been destroyed.
         */
        this._onDestroy = new Subject();
        this._disableRipple = false;
    }
    /**
     * Background color of the tab nav.
     * @return {?}
     */
    get backgroundColor() { return this._backgroundColor; }
    /**
     * @param {?} value
     * @return {?}
     */
    set backgroundColor(value) {
        let /** @type {?} */ nativeElement = this._elementRef.nativeElement;
        this._renderer.removeClass(nativeElement, `mat-background-${this.backgroundColor}`);
        if (value) {
            this._renderer.addClass(nativeElement, `mat-background-${value}`);
        }
        this._backgroundColor = value;
    }
    /**
     * Whether ripples should be disabled for all links or not.
     * @return {?}
     */
    get disableRipple() { return this._disableRipple; }
    /**
     * @param {?} value
     * @return {?}
     */
    set disableRipple(value) {
        this._disableRipple = coerceBooleanProperty(value);
        this._setLinkDisableRipple();
    }
    /**
     * Notifies the component that the active link has been changed.
     * @param {?} element
     * @return {?}
     */
    updateActiveLink(element) {
        this._activeLinkChanged = this._activeLinkElement != element;
        this._activeLinkElement = element;
        if (this._activeLinkChanged) {
            this._changeDetectorRef.markForCheck();
        }
    }
    /**
     * @return {?}
     */
    ngAfterContentInit() {
        this._ngZone.runOutsideAngular(() => {
            const /** @type {?} */ dirChange = this._dir ? this._dir.change : observableOf(null);
            return takeUntil.call(merge(dirChange, this._viewportRuler.change(10)), this._onDestroy)
                .subscribe(() => this._alignInkBar());
        });
        this._setLinkDisableRipple();
    }
    /**
     * Checks if the active link has been changed and, if so, will update the ink bar.
     * @return {?}
     */
    ngAfterContentChecked() {
        if (this._activeLinkChanged) {
            this._alignInkBar();
            this._activeLinkChanged = false;
        }
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }
    /**
     * Aligns the ink bar to the active link.
     * @return {?}
     */
    _alignInkBar() {
        if (this._activeLinkElement) {
            this._inkBar.alignToElement(this._activeLinkElement.nativeElement);
        }
    }
    /**
     * Sets the `disableRipple` property on each link of the navigation bar.
     * @return {?}
     */
    _setLinkDisableRipple() {
        if (this._tabLinks) {
            this._tabLinks.forEach(link => link.disableRipple = this.disableRipple);
        }
    }
}
MatTabNav.decorators = [
    { type: Component, args: [{selector: '[mat-tab-nav-bar]',
                exportAs: 'matTabNavBar, matTabNav',
                inputs: ['color', 'disableRipple'],
                template: "<div class=\"mat-tab-links\" (cdkObserveContent)=\"_alignInkBar()\"><ng-content></ng-content><mat-ink-bar></mat-ink-bar></div>",
                styles: [".mat-tab-nav-bar{overflow:hidden;position:relative;flex-shrink:0}.mat-tab-links{position:relative}.mat-tab-link{height:48px;padding:0 24px;cursor:pointer;box-sizing:border-box;opacity:.6;min-width:160px;text-align:center;display:inline-flex;justify-content:center;align-items:center;white-space:nowrap;vertical-align:top;text-decoration:none;position:relative;overflow:hidden}.mat-tab-link:focus{outline:0;opacity:1}.mat-tab-link.mat-tab-disabled{cursor:default;pointer-events:none}@media (max-width:600px){.mat-tab-link{min-width:72px}}.mat-ink-bar{position:absolute;bottom:0;height:2px;transition:.5s cubic-bezier(.35,0,.25,1)}.mat-tab-group-inverted-header .mat-ink-bar{bottom:auto;top:0}"],
                host: { 'class': 'mat-tab-nav-bar' },
                encapsulation: ViewEncapsulation.None,
                preserveWhitespaces: false,
                changeDetection: ChangeDetectionStrategy.OnPush,
            },] },
];
/**
 * @nocollapse
 */
MatTabNav.ctorParameters = () => [
    { type: Renderer2, },
    { type: ElementRef, },
    { type: Directionality, decorators: [{ type: Optional },] },
    { type: NgZone, },
    { type: ChangeDetectorRef, },
    { type: ViewportRuler, },
];
MatTabNav.propDecorators = {
    '_inkBar': [{ type: ViewChild, args: [MatInkBar,] },],
    '_tabLinks': [{ type: ContentChildren, args: [forwardRef(() => MatTabLink), { descendants: true },] },],
    'backgroundColor': [{ type: Input },],
};
function MatTabNav_tsickle_Closure_declarations() {
    /** @type {?} */
    MatTabNav.decorators;
    /**
     * @nocollapse
     * @type {?}
     */
    MatTabNav.ctorParameters;
    /** @type {?} */
    MatTabNav.propDecorators;
    /**
     * Subject that emits when the component has been destroyed.
     * @type {?}
     */
    MatTabNav.prototype._onDestroy;
    /** @type {?} */
    MatTabNav.prototype._activeLinkChanged;
    /** @type {?} */
    MatTabNav.prototype._activeLinkElement;
    /** @type {?} */
    MatTabNav.prototype._inkBar;
    /**
     * Query list of all tab links of the tab navigation.
     * @type {?}
     */
    MatTabNav.prototype._tabLinks;
    /** @type {?} */
    MatTabNav.prototype._backgroundColor;
    /** @type {?} */
    MatTabNav.prototype._disableRipple;
    /** @type {?} */
    MatTabNav.prototype._dir;
    /** @type {?} */
    MatTabNav.prototype._ngZone;
    /** @type {?} */
    MatTabNav.prototype._changeDetectorRef;
    /** @type {?} */
    MatTabNav.prototype._viewportRuler;
}
export class MatTabLinkBase {
}
export const /** @type {?} */ _MatTabLinkMixinBase = mixinDisabled(MatTabLinkBase);
/**
 * Link inside of a `mat-tab-nav-bar`.
 */
export class MatTabLink extends _MatTabLinkMixinBase {
    /**
     * @param {?} _tabNavBar
     * @param {?} _elementRef
     * @param {?} ngZone
     * @param {?} platform
     * @param {?} globalOptions
     */
    constructor(_tabNavBar, _elementRef, ngZone, platform, globalOptions) {
        super();
        this._tabNavBar = _tabNavBar;
        this._elementRef = _elementRef;
        /**
         * Whether the tab link is active or not.
         */
        this._isActive = false;
        /**
         * Whether the ripples for this tab should be disabled or not.
         */
        this._disableRipple = false;
        // Manually create a ripple instance that uses the tab link element as trigger element.
        // Notice that the lifecycle hooks for the ripple config won't be called anymore.
        this._tabLinkRipple = new MatRipple(_elementRef, ngZone, platform, globalOptions);
    }
    /**
     * Whether the link is active.
     * @return {?}
     */
    get active() { return this._isActive; }
    /**
     * @param {?} value
     * @return {?}
     */
    set active(value) {
        this._isActive = value;
        if (value) {
            this._tabNavBar.updateActiveLink(this._elementRef);
        }
    }
    /**
     * Whether ripples should be disabled or not.
     * @return {?}
     */
    get disableRipple() { return this._disableRipple; }
    /**
     * @param {?} value
     * @return {?}
     */
    set disableRipple(value) {
        this._disableRipple = value;
        this._tabLinkRipple.disabled = this.disableRipple;
        this._tabLinkRipple._updateRippleRenderer();
    }
    /**
     * \@docs-private
     * @return {?}
     */
    get tabIndex() {
        return this.disabled ? -1 : 0;
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        // Manually call the ngOnDestroy lifecycle hook of the ripple instance because it won't be
        // called automatically since its instance is not created by Angular.
        this._tabLinkRipple.ngOnDestroy();
    }
}
MatTabLink.decorators = [
    { type: Directive, args: [{
                selector: '[mat-tab-link], [matTabLink]',
                exportAs: 'matTabLink',
                inputs: ['disabled'],
                host: {
                    'class': 'mat-tab-link',
                    '[attr.aria-disabled]': 'disabled.toString()',
                    '[attr.tabindex]': 'tabIndex',
                    '[class.mat-tab-disabled]': 'disabled',
                    '[class.mat-tab-label-active]': 'active',
                }
            },] },
];
/**
 * @nocollapse
 */
MatTabLink.ctorParameters = () => [
    { type: MatTabNav, },
    { type: ElementRef, },
    { type: NgZone, },
    { type: Platform, },
    { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MAT_RIPPLE_GLOBAL_OPTIONS,] },] },
];
MatTabLink.propDecorators = {
    'active': [{ type: Input },],
};
function MatTabLink_tsickle_Closure_declarations() {
    /** @type {?} */
    MatTabLink.decorators;
    /**
     * @nocollapse
     * @type {?}
     */
    MatTabLink.ctorParameters;
    /** @type {?} */
    MatTabLink.propDecorators;
    /**
     * Whether the tab link is active or not.
     * @type {?}
     */
    MatTabLink.prototype._isActive;
    /**
     * Whether the ripples for this tab should be disabled or not.
     * @type {?}
     */
    MatTabLink.prototype._disableRipple;
    /**
     * Reference to the instance of the ripple for the tab link.
     * @type {?}
     */
    MatTabLink.prototype._tabLinkRipple;
    /** @type {?} */
    MatTabLink.prototype._tabNavBar;
    /** @type {?} */
    MatTabLink.prototype._elementRef;
}
//# sourceMappingURL=tab-nav-bar.js.map